import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";

import { BasicGroup } from "config/constants.config";
import { buildBasicItemsSelector } from "store/associatedItems";

/**
 * Salesman prompts shown in the Items step (added 2026-10-16, repricing package):
 *  - "crane" when a 2-story / 3-story charge is added: asks whether a crane is needed and offers to add the crane line;
 *  - "flat" when a flat-roof (modified bitumen cap sheet) item is added: explains the automatically added extras.
 * Prompts only fire for items added during the current editing session (never for items already on the estimate).
 */
const STORY_RE = /^\s*[23]\s*STORY CHARGE/i;
const FLAT_RE = /^\s*MODIFIED BITUMEN SELF-ADHERED CAP SHEET/i;
export const CRANE_ITEM_NAME = "CRANE (2 OR 3 STORY ACCESS)";
// extra cap-sheet rolls for parapets / walls / cants (catalog basic material item, added 2026-10-17)
export const CAP_EXTRA_ITEM_NAME = "MODIFIED BITUMEN CAP SHEET - PARAPET/CANT EXTRA | 1 SQ";
const ROLL_COVERAGE_SQFT = 100; // 1 SQ cap sheet roll
const DEFAULT_FLASHING_HEIGHT_FT = 2; // cant + flashing up the wall
const normName = (n: string) => (n || "").replace(/\s+/g, " ").trim().toUpperCase();

const useStyles = makeStyles({
  paper: { borderRadius: 8, maxWidth: 520 },
  title: { paddingBottom: 4 },
  accent: { borderLeft: "4px solid #ff9800", paddingLeft: 12, margin: "4px 0 12px" },
  list: { margin: "8px 0 0", paddingLeft: 20 },
  actions: { padding: "8px 24px 16px" }
});

export default function PricingPrompts({ estimate, items, patch }) {
  const classes = useStyles();
  const [prompt, setPrompt] = useState<null | "crane" | "flat">(null);
  const [addCraneFlat, setAddCraneFlat] = useState(true); // flat roofs: crane line ON by default, salesman can switch it off
  const [cantLf, setCantLf] = useState("");
  const [wallLf, setWallLf] = useState("");
  const [heightFt, setHeightFt] = useState(String(DEFAULT_FLASHING_HEIGHT_FT));
  const materialsSelector = useMemo(() => buildBasicItemsSelector(BasicGroup.MATERIALS, estimate.type), [estimate.type]);
  const basicMaterials = useSelector(materialsSelector);
  const capExtraItem = useMemo(
    () => (basicMaterials || []).find((i: any) => normName(i.name) === CAP_EXTRA_ITEM_NAME),
    [basicMaterials]
  );
  // extra cap rolls = ceil((cant LF + wall LF) x flashing height ft / 100 sq ft per roll)
  const extraRolls = Math.ceil((((+cantLf || 0) + (+wallLf || 0)) * (+heightFt || 0)) / ROLL_COVERAGE_SQFT - 1e-9);
  const previous = useRef<Set<any> | null>(null);
  const byId = useMemo(() => {
    const map: any = {};
    (items || []).forEach((i: any) => { map[i.id] = i; });
    return map;
  }, [items]);
  const craneItem = useMemo(
    () => (items || []).find((i: any) => (i.name || "").trim().toUpperCase() === CRANE_ITEM_NAME),
    [items]
  );

  useEffect(() => {
    if (!items || !items.length) return; // catalog not loaded yet
    const current = new Set<any>((estimate.items || []).filter(i => i.quantity > 0).map(i => i.id));
    if (previous.current === null) {
      previous.current = current; // first render = what is already saved, no prompts
      return;
    }
    const added = Array.from(current).filter((id: any) => !previous.current.has(id));
    previous.current = current;
    const names = added.map((id: any) => (byId[id] && byId[id].name) || "");
    const craneAlready = craneItem && current.has(craneItem.id);

    if (names.some(n => STORY_RE.test(n)) && craneItem && !craneAlready)
      setPrompt("crane");
    else if (names.some(n => FLAT_RE.test(n))) {
      setAddCraneFlat(true);
      setCantLf("");
      setWallLf("");
      setHeightFt(String(DEFAULT_FLASHING_HEIGHT_FT));
      setPrompt("flat");
    }
  }, [estimate.items, items]);

  const addCrane = () => {
    if (craneItem && !(estimate.items || []).some(i => i.id === craneItem.id))
      patch({ items: [...(estimate.items || []), { id: craneItem.id, quantity: 1 }] });
    setPrompt(null);
  };

  const confirmFlat = () => {
    const patchObj: any = {};
    if (addCraneFlat && craneItem && !(estimate.items || []).some(i => i.id === craneItem.id && i.quantity > 0))
      patchObj.items = [...(estimate.items || []).filter(i => i.id !== craneItem.id), { id: craneItem.id, quantity: 1 }];
    if (extraRolls > 0 && capExtraItem)
      patchObj.materials = [
        ...(estimate.materials || []).filter(l => l.id !== capExtraItem.id),
        { id: capExtraItem.id, quantity: extraRolls, custom: true } // custom => survives the server's re-expansion of materials
      ];
    if (Object.keys(patchObj).length)
      patch(patchObj);
    setPrompt(null);
  };

  return (
    <>
      <Dialog open={prompt === "crane"} onClose={() => setPrompt(null)} classes={{ paper: classes.paper }}>
        <DialogTitle className={classes.title}>Crane needed?</DialogTitle>
        <DialogContent>
          <div className={classes.accent}>
            <Typography variant="body1">
              You added a 2-story / 3-story charge. High roofs often need a crane to get shingles up, which costs about $250.
            </Typography>
          </div>
          <Typography variant="body2" color="textSecondary">
            Add the crane line ($250) to this estimate, or continue if the crew can load the roof without one.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button onClick={() => setPrompt(null)} color="default">No crane needed</Button>
          <Button onClick={addCrane} color="primary" variant="contained">Add crane line ($250)</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={prompt === "flat"} onClose={() => setPrompt(null)} classes={{ paper: classes.paper }}>
        <DialogTitle className={classes.title}>Flat roof: lines will be added</DialogTitle>
        <DialogContent>
          <div className={classes.accent}>
            <Typography variant="body1">
              Flat roofs also need angle flashing and asphalt primer. These lines are added to the materials order when you save the estimate:
            </Typography>
          </div>
          <ul className={classes.list}>
            <li><Typography variant="body2">Angle flashing: 1 piece (10') per flat square</Typography></li>
            <li><Typography variant="body2">Asphalt primer: 1 can per 15 flat squares (minimum 1)</Typography></li>
          </ul>

          <Typography variant="subtitle2" style={{ marginTop: 16 }}>Crane</Typography>
          <FormControlLabel
            control={<Checkbox color="primary" checked={addCraneFlat} disabled={!craneItem} onChange={e => setAddCraneFlat(e.target.checked)} />}
            label="Add crane line ($250)"
          />
          <Typography variant="body2" color="textSecondary">
            Supplier crane charges ($250) were billed on recent flat-roof jobs. Untick if the roof can be loaded without a crane.
          </Typography>

          <Typography variant="subtitle2" style={{ marginTop: 16 }}>Parapets, walls and cants (more cap sheet)</Typography>
          <Typography variant="body2" color="textSecondary">
            The standard formula is 1 cap roll per square and does not cover walls. Enter each wall once: cant LF for walls with a cant strip,
            wall flashing LF for walls without. Extra rolls = (cant + wall LF) x height / 100 sq ft per roll.
          </Typography>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <TextField label="Cant strip LF" type="number" value={cantLf} onChange={e => setCantLf(e.target.value)} inputProps={{ min: 0 }} />
            <TextField label="Parapet / wall flashing LF" type="number" value={wallLf} onChange={e => setWallLf(e.target.value)} inputProps={{ min: 0 }} />
            <TextField label="Flashing height (ft)" type="number" value={heightFt} onChange={e => setHeightFt(e.target.value)} inputProps={{ min: 0, step: 0.5 }} />
          </div>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {extraRolls > 0
              ? (capExtraItem
                ? `Adds ${extraRolls} extra cap sheet roll${extraRolls > 1 ? "s" : ""} (about $${(extraRolls * 136).toFixed(0)} cost) to the materials order.`
                : "The extra cap sheet item is not in the catalog yet: add the rolls by hand in the Materials step.")
              : "No extra rolls (leave blank if there are no walls or cants)."}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            After saving, open the Materials step and confirm the quantities. Increase them if this roof has extra edges, walls, curbs or drains.
            Tapered ISO insulation and masonry anchors are in the catalog (Materials step, add item) when the job needs them.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button onClick={() => setPrompt(null)} color="default">Skip</Button>
          <Button onClick={confirmFlat} color="primary" variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
